
import * as THREE from "../../libs/three.js/build/three.module.js";
import {CrossSectionProfile} from "./CrossSectionProfile.js";
import {Utils} from "../utils.js";
import {EventDispatcher} from "../EventDispatcher.js";

export class CrossSectionTool extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.renderer = viewer.renderer;

		this.addEventListener("start_inserting_cross_section", e => {
			this.viewer.dispatchEvent({
				type: "cancel_insertions"
			});
		});

		this.toolScene = new THREE.Scene();
		this.toolScene.name = "scene_cross_section";
		this.light = new THREE.PointLight(0xffffff, 1.0);
		this.toolScene.add(this.light);

		this.viewer.inputHandler.registerInteractiveScene(this.toolScene);

		this.onRemove = e => {
			this.toolScene.remove(e.profile);
			if (e.profile._lineObjects) {
				for (let obj of e.profile._lineObjects) {
					this.toolScene.remove(obj);
				}
				e.profile._lineObjects = null;
			}
		};
		this.onAdd = e => this.toolScene.add(e.profile);

		for (let p of viewer.scene.crossSectionProfiles) {
			this.onAdd({profile: p});
		}

		viewer.addEventListener("update", this.update.bind(this));
		viewer.addEventListener("render.pass.perspective_overlay", this.render.bind(this));
		viewer.addEventListener("scene_changed", this.onSceneChange.bind(this));

		viewer.scene.addEventListener("cross_section_added", this.onAdd);
		viewer.scene.addEventListener("cross_section_removed", this.onRemove);
	}

	onSceneChange(e) {
		if (e.oldScene) {
			e.oldScene.removeEventListeners("cross_section_added", this.onAdd);
			e.oldScene.removeEventListeners("cross_section_removed", this.onRemove);
		}

		e.scene.addEventListener("cross_section_added", this.onAdd);
		e.scene.addEventListener("cross_section_removed", this.onRemove);
	}

	setScene(scene) {
		// No-op: CrossSectionTool manages its own toolScene
	}

	startInsertion() {
		let domElement = this.viewer.renderer.domElement;
		let canvasSize = this.viewer.renderer.getSize(new THREE.Vector2());

		// Create SVG overlay for drawing feedback
		let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svg.setAttribute("width", canvasSize.width);
		svg.setAttribute("height", canvasSize.height);
		svg.style.position = "absolute";
		svg.style.pointerEvents = "none";
		svg.style.top = "0";
		svg.style.left = "0";
		domElement.parentElement.appendChild(svg);

		// Status text
		let statusText = document.createElementNS("http://www.w3.org/2000/svg", "text");
		statusText.setAttribute("x", "20");
		statusText.setAttribute("y", "30");
		statusText.setAttribute("fill", "white");
		statusText.setAttribute("stroke", "black");
		statusText.setAttribute("stroke-width", "0.5");
		statusText.setAttribute("font-size", "16");
		statusText.setAttribute("font-family", "sans-serif");
		statusText.textContent = "Click START point of transect";
		svg.appendChild(statusText);

		// Transect line (green dashed)
		let transectLineSvg = document.createElementNS("http://www.w3.org/2000/svg", "line");
		transectLineSvg.setAttribute("stroke", "rgb(0,204,102)");
		transectLineSvg.setAttribute("stroke-width", "3");
		transectLineSvg.setAttribute("stroke-dasharray", "6, 4");
		transectLineSvg.style.display = "none";
		svg.appendChild(transectLineSvg);

		// Preview line from start to cursor
		let previewLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
		previewLine.setAttribute("stroke", "rgba(255,255,0,0.8)");
		previewLine.setAttribute("stroke-width", "2");
		previewLine.setAttribute("stroke-dasharray", "4, 4");
		previewLine.style.display = "none";
		svg.appendChild(previewLine);

		// Vertex markers
		let vertexMarkers = document.createElementNS("http://www.w3.org/2000/svg", "g");
		svg.appendChild(vertexMarkers);

		let profile = new CrossSectionProfile();

		this.dispatchEvent({
			type: "start_inserting_cross_section",
			profile: profile
		});

		this.viewer.scene.addCrossSectionProfile(profile);

		let startPoint = null; // { screenX, screenY, worldPos }

		let addVertexMarker = (x, y, color) => {
			let circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
			circle.setAttribute("cx", x);
			circle.setAttribute("cy", y);
			circle.setAttribute("r", "6");
			circle.setAttribute("fill", color);
			circle.setAttribute("stroke", "white");
			circle.setAttribute("stroke-width", "2");
			vertexMarkers.appendChild(circle);
		};

		let onMouseMove = (e) => {
			if (!startPoint) { return; }

			let rect = domElement.getBoundingClientRect();
			let x = e.clientX - rect.left;
			let y = e.clientY - rect.top;

			previewLine.setAttribute("x1", startPoint.screenX);
			previewLine.setAttribute("y1", startPoint.screenY);
			previewLine.setAttribute("x2", x);
			previewLine.setAttribute("y2", y);
			previewLine.style.display = "";
		};

		let finalize = () => {
			domElement.removeEventListener("mouseup", onMouseUp, true);
			domElement.removeEventListener("mousemove", onMouseMove, true);
			domElement.removeEventListener("contextmenu", onContextMenu, true);
			this.viewer.removeEventListener("cancel_insertions", cancel);
			svg.remove();
			this.viewer.inputHandler.enabled = true;
		};

		let cancel = () => {
			this.viewer.scene.removeCrossSectionProfile(profile);
			finalize();
		};

		let complete = () => {
			profile.phase = "done";
			finalize();
			this.computeProfile(profile);
		};

		let onMouseUp = (e) => {
			if (e.button === 0) {
				let rect = domElement.getBoundingClientRect();
				let x = e.clientX - rect.left;
				let y = e.clientY - rect.top;

				let mouse = new THREE.Vector2(x, y);
				let cam = this.viewer.scene.getActiveCamera();
				let pointclouds = this.viewer.scene.pointclouds;
				let I = Utils.getMousePointCloudIntersection(
					mouse, cam, this.viewer, pointclouds
				);

				if (!I) { return; }

				let worldPos = I.location.clone();

				if (!startPoint) {
					// First click: start point
					startPoint = { screenX: x, screenY: y, worldPos };
					profile.transectLine.start = worldPos;
					addVertexMarker(x, y, "rgb(0,204,102)");
					statusText.textContent = "Click END point of transect";
				} else {
					// Second click: end point
					profile.transectLine.end = worldPos;
					addVertexMarker(x, y, "rgb(0,204,102)");

					// Draw final transect line
					transectLineSvg.setAttribute("x1", startPoint.screenX);
					transectLineSvg.setAttribute("y1", startPoint.screenY);
					transectLineSvg.setAttribute("x2", x);
					transectLineSvg.setAttribute("y2", y);
					transectLineSvg.style.display = "";

					complete();
				}
			} else if (e.button === 2) {
				cancel();
			}
		};

		let onContextMenu = (e) => {
			e.preventDefault();
		};

		this.viewer.addEventListener("cancel_insertions", cancel);
		domElement.addEventListener("mouseup", onMouseUp, true);
		domElement.addEventListener("mousemove", onMouseMove, true);
		domElement.addEventListener("contextmenu", onContextMenu, true);

		// Disable viewer input during drawing
		this.viewer.inputHandler.enabled = false;

		return profile;
	}

	computeProfile(profile) {
		let start = profile.transectLine.start;
		let end = profile.transectLine.end;
		let halfSwath = profile.swathWidth / 2;

		// Transect direction and length (2D, XY plane)
		let dx = end.x - start.x;
		let dy = end.y - start.y;
		let transectLength = Math.sqrt(dx * dx + dy * dy);
		profile.length = transectLength;

		if (transectLength < 0.001) { return; }

		// Normalized transect direction (2D)
		let dirX = dx / transectLength;
		let dirY = dy / transectLength;

		// Perpendicular direction (2D)
		let perpX = -dirY;
		let perpY = dirX;

		// Collect all points within the swath
		let collectedPoints = [];
		let pointclouds = this.viewer.scene.pointclouds;

		for (let pointcloud of pointclouds) {
			if (!pointcloud.visible) { continue; }

			let nodes = pointcloud.visibleNodes;
			if (!nodes) { continue; }

			for (let node of nodes) {
				let geometry = node.geometryNode.geometry;
				if (!geometry) { continue; }

				let positionAttribute = geometry.attributes.position;
				if (!positionAttribute) { continue; }

				let nodeWorld = node.sceneNode.matrixWorld;
				let positions = positionAttribute.array;
				let numPoints = positionAttribute.count;
				let classificationAttr = geometry.attributes.classification;
				let classificationArray = classificationAttr ? classificationAttr.array : null;

				for (let i = 0; i < numPoints; i++) {
					// Skip points whose classification is filtered out
					if (classificationArray) {
						let classId = classificationArray[i];
						let classEntry = this.viewer.classifications[classId];
						if (classEntry && classEntry.visible === false) {
							continue;
						}
					}

					let lx = positions[i * 3 + 0];
					let ly = positions[i * 3 + 1];
					let lz = positions[i * 3 + 2];

					// Transform to world space
					let e = nodeWorld.elements;
					let wx = e[0] * lx + e[4] * ly + e[8] * lz + e[12];
					let wy = e[1] * lx + e[5] * ly + e[9] * lz + e[13];
					let wz = e[2] * lx + e[6] * ly + e[10] * lz + e[14];

					// Vector from start to point (2D)
					let toX = wx - start.x;
					let toY = wy - start.y;

					// Project onto transect axis
					let along = toX * dirX + toY * dirY;
					if (along < 0 || along > transectLength) { continue; }

					// Perpendicular distance
					let perp = Math.abs(toX * perpX + toY * perpY);
					if (perp > halfSwath) { continue; }

					collectedPoints.push({ distanceAlong: along, elevation: wz });
				}
			}
		}

		if (collectedPoints.length === 0) { return; }

		// Z outlier filtering (std dev from median)
		let filteredPoints = collectedPoints;
		profile.outlierFilter.pointsExcluded = 0;

		if (profile.outlierFilter.enabled) {
			filteredPoints = this.filterOutliers(
				collectedPoints, profile.outlierFilter.threshold
			);
			profile.outlierFilter.pointsExcluded =
				collectedPoints.length - filteredPoints.length;
		}

		if (filteredPoints.length === 0) { return; }

		// Raw points are the actual LiDAR positions (for scatter display)
		profile.pointsRaw = filteredPoints;

		// Fine-bin to collapse co-located points into one median elevation
		// per position. This prevents zig-zag from multiple elevations at
		// the same distance (spread across the swath width).
		let binnedPoints = this.fineBinMedian(filteredPoints, transectLength);

		// Detect curb reveals BEFORE smoothing — we need zone boundaries
		// to segment the profile for independent smoothing
		this.detectCurbRevealIntersections(profile, halfSwath, dirX, dirY, perpX, perpY);

		// Segment-aware smoothing: splits at curb zones, smooths each
		// road/sidewalk segment independently, inserts right-angle steps.
		// Falls back to full Gaussian if no curb zones are detected.
		profile.points = this.smoothSegmented(binnedPoints, profile.intersectingCurbReveals, 0.6);

		// Detect intersecting landmarks
		this.detectLandmarkIntersections(profile, halfSwath, dirX, dirY, perpX, perpY);

		// Compute metrics (high/low points, slopes)
		this.computeMetrics(profile);

		// Apply datum offset if available
		this.applyDatumOffset(profile);

		profile.initialized = true;

		// Add 3D visualization
		this.addTransectVisualization(profile);

		// Open chart window
		if (this.viewer.crossSectionWindow) {
			this.viewer.crossSectionWindow.show(profile);
		}
	}

	recomputeProfile(profile) {
		if (!profile || !profile.transectLine.start || !profile.transectLine.end) {
			return;
		}

		// Clear previous visualization
		if (profile._lineObjects) {
			for (let obj of profile._lineObjects) {
				this.toolScene.remove(obj);
			}
			profile._lineObjects = null;
		}

		// Clear computed data
		profile.points = [];
		profile.pointsRaw = [];
		profile.intersectingLandmarks = [];
		profile.intersectingCurbReveals = [];
		profile.computedMetrics = { highPoint: null, lowPoint: null, slopes: [] };
		profile.datumOffset = { applied: false, referenceLandmarkId: null, offsetValue: null };
		profile.initialized = false;

		this.computeProfile(profile);
	}

	// Std-dev outlier filtering: exclude points beyond N std devs from median Z
	filterOutliers(points, threshold) {
		let zValues = points.map(p => p.elevation);
		zValues.sort((a, b) => a - b);

		let median = this.percentile(zValues, 0.5);

		// Compute std deviation
		let sumSqDiff = 0;
		for (let z of zValues) {
			let diff = z - median;
			sumSqDiff += diff * diff;
		}
		let stdDev = Math.sqrt(sumSqDiff / zValues.length);

		if (stdDev < 0.001) { return points; }

		let lower = median - threshold * stdDev;
		let upper = median + threshold * stdDev;

		return points.filter(p => p.elevation >= lower && p.elevation <= upper);
	}

	// Moving average smoothing
	smoothMovingAverage(points, windowSize) {
		if (points.length <= windowSize) { return points.slice(); }

		let halfWindow = Math.floor(windowSize / 2);
		let smoothed = [];

		for (let i = 0; i < points.length; i++) {
			let start = Math.max(0, i - halfWindow);
			let end = Math.min(points.length - 1, i + halfWindow);
			let sum = 0;
			let count = 0;

			for (let j = start; j <= end; j++) {
				sum += points[j].elevation;
				count++;
			}

			smoothed.push({
				distanceAlong: points[i].distanceAlong,
				elevation: sum / count
			});
		}

		return smoothed;
	}

	// Fine-bin points to produce one median elevation per distance step.
	// Uses many small bins (~1 inch) so spatial resolution is preserved,
	// but co-located points at different elevations collapse to a single value.
	fineBinMedian(points, transectLength) {
		let binSize = 0.025; // meters (~1 inch)
		let numBins = Math.max(20, Math.ceil(transectLength / binSize));
		let bins = new Array(numBins);
		for (let i = 0; i < numBins; i++) { bins[i] = []; }

		for (let p of points) {
			let idx = Math.floor(p.distanceAlong / transectLength * numBins);
			if (idx >= numBins) { idx = numBins - 1; }
			if (idx < 0) { idx = 0; }
			bins[idx].push(p.elevation);
		}

		let result = [];
		for (let b = 0; b < numBins; b++) {
			if (bins[b].length === 0) { continue; }
			bins[b].sort((a, c) => a - c);
			let median = this.percentile(bins[b], 0.5);
			let distance = (b + 0.5) / numBins * transectLength;
			result.push({ distanceAlong: distance, elevation: median });
		}

		// Local outlier rejection: remove bins whose median deviates
		// significantly from their neighborhood median. This cleans up
		// spikes around manholes, catch basins, etc.
		let neighborRadius = 5;
		let cleaned = [];
		for (let i = 0; i < result.length; i++) {
			let start = Math.max(0, i - neighborRadius);
			let end = Math.min(result.length - 1, i + neighborRadius);
			let neighborElevs = [];
			for (let j = start; j <= end; j++) {
				neighborElevs.push(result[j].elevation);
			}
			neighborElevs.sort((a, c) => a - c);
			let localMedian = neighborElevs[Math.floor(neighborElevs.length / 2)];

			let diff = Math.abs(result[i].elevation - localMedian);
			// Allow through if within 0.05m (~2 inches) of local median
			if (diff < 0.05) {
				cleaned.push(result[i]);
			}
		}

		return cleaned;
	}

	// Gaussian filter: pure spatial smoothing weighted by distance.
	// Points must be sorted by distanceAlong.
	//   sigma — spatial reach in meters (higher = smoother)
	smoothGaussian(points, sigma) {
		if (points.length < 3) { return points.slice(); }

		let cutoff = sigma * 3;
		let invS2 = -0.5 / (sigma * sigma);
		let smoothed = [];

		for (let i = 0; i < points.length; i++) {
			let centerDist = points[i].distanceAlong;
			let weightSum = 0;
			let valueSum = 0;

			// Scan left
			for (let j = i; j >= 0; j--) {
				let dDist = points[j].distanceAlong - centerDist;
				if (dDist < -cutoff) { break; }
				let w = Math.exp(dDist * dDist * invS2);
				weightSum += w;
				valueSum += w * points[j].elevation;
			}

			// Scan right (skip center, already counted)
			for (let j = i + 1; j < points.length; j++) {
				let dDist = points[j].distanceAlong - centerDist;
				if (dDist > cutoff) { break; }
				let w = Math.exp(dDist * dDist * invS2);
				weightSum += w;
				valueSum += w * points[j].elevation;
			}

			smoothed.push({
				distanceAlong: centerDist,
				elevation: valueSum / weightSum,
			});
		}

		return smoothed;
	}

	// Segment-aware smoothing: splits binned points at curb zone boundaries,
	// Gaussian-smooths each non-curb segment independently, then inserts
	// right-angle steps for curb zones and concatenates everything.
	smoothSegmented(binnedPoints, curbReveals, sigma) {
		// Only use curb reveals that have zone data
		let zones = curbReveals.filter(cr => cr.zone_start != null && cr.zone_end != null);

		// No curb zones? Fall back to full Gaussian smoothing
		if (zones.length === 0) {
			return this.smoothGaussian(binnedPoints, sigma);
		}

		// Sort zones by zone_start
		zones.sort((a, b) => a.zone_start - b.zone_start);

		// Segment binned points into non-curb segments
		// Points inside a curb zone are excluded from smoothing
		let segments = []; // array of arrays of points
		let currentSegment = [];

		for (let p of binnedPoints) {
			let inCurbZone = false;
			for (let z of zones) {
				if (p.distanceAlong >= z.zone_start && p.distanceAlong <= z.zone_end) {
					inCurbZone = true;
					break;
				}
			}

			if (inCurbZone) {
				// End current segment if it has points
				if (currentSegment.length > 0) {
					segments.push(currentSegment);
					currentSegment = [];
				}
			} else {
				currentSegment.push(p);
			}
		}
		// Push final segment
		if (currentSegment.length > 0) {
			segments.push(currentSegment);
		}

		// Gaussian smooth each segment independently
		let smoothedSegments = segments.map(seg => this.smoothGaussian(seg, sigma));

		// Build the final profile by interleaving smoothed segments with curb steps
		let result = [];
		let epsilon = 0.005; // ~0.2 inches, tiny gap for vertical step rendering

		// Track which zone comes next between segments
		let zoneIdx = 0;
		for (let si = 0; si < smoothedSegments.length; si++) {
			let seg = smoothedSegments[si];
			// Add this segment's points
			for (let p of seg) {
				result.push(p);
			}

			// After this segment, insert curb steps for any zones that fall
			// between this segment and the next
			let segEnd = seg[seg.length - 1].distanceAlong;
			let nextSegStart = (si + 1 < smoothedSegments.length)
				? smoothedSegments[si + 1][0].distanceAlong
				: Infinity;

			while (zoneIdx < zones.length && zones[zoneIdx].zone_start >= segEnd - epsilon
				&& zones[zoneIdx].zone_end <= nextSegStart + epsilon) {
				let zone = zones[zoneIdx];

				// Elevation at the end of the segment before the curb
				let beforeElev = seg[seg.length - 1].elevation;

				// Elevation at the start of the segment after the curb
				let afterElev = (si + 1 < smoothedSegments.length && smoothedSegments[si + 1].length > 0)
					? smoothedSegments[si + 1][0].elevation
					: beforeElev;

				// Insert right-angle step:
				// horizontal at before_elev, vertical drop, horizontal at after_elev
				result.push({ distanceAlong: zone.zone_start + epsilon, elevation: beforeElev });
				result.push({ distanceAlong: zone.zone_end - epsilon, elevation: afterElev });

				zoneIdx++;
			}
		}

		// Sort by distance to ensure proper ordering
		result.sort((a, b) => a.distanceAlong - b.distanceAlong);

		return result;
	}

	// Bilateral filter smoothing: blends neighbors weighted by both spatial
	// distance and elevation similarity. Smooths noise while preserving
	// sharp transitions (curbs, gutters) where elevation changes abruptly.
	// Points must be sorted by distanceAlong.
	//   sigmaDist  — spatial reach in meters (higher = smoother)
	//   sigmaElev  — elevation tolerance in meters (higher = more blurring across edges)
	smoothBilateral(points, sigmaDist, sigmaElev) {
		if (points.length < 3) { return points.slice(); }

		let cutoff = sigmaDist * 2.5;
		let invSd2 = -0.5 / (sigmaDist * sigmaDist);
		let invSe2 = -0.5 / (sigmaElev * sigmaElev);
		let smoothed = [];

		for (let i = 0; i < points.length; i++) {
			let centerDist = points[i].distanceAlong;
			let centerElev = points[i].elevation;
			let weightSum = 0;
			let valueSum = 0;

			// Scan left
			for (let j = i; j >= 0; j--) {
				let dDist = points[j].distanceAlong - centerDist;
				if (dDist < -cutoff) { break; }
				let dElev = points[j].elevation - centerElev;
				let w = Math.exp(dDist * dDist * invSd2 + dElev * dElev * invSe2);
				weightSum += w;
				valueSum += w * points[j].elevation;
			}

			// Scan right (skip center, already counted)
			for (let j = i + 1; j < points.length; j++) {
				let dDist = points[j].distanceAlong - centerDist;
				if (dDist > cutoff) { break; }
				let dElev = points[j].elevation - centerElev;
				let w = Math.exp(dDist * dDist * invSd2 + dElev * dElev * invSe2);
				weightSum += w;
				valueSum += w * points[j].elevation;
			}

			smoothed.push({
				distanceAlong: centerDist,
				elevation: valueSum / weightSum,
			});
		}

		return smoothed;
	}

	// Detect FeatureZone (landmark) intersections with the swath rectangle
	detectLandmarkIntersections(profile, halfSwath, dirX, dirY, perpX, perpY) {
		let start = profile.transectLine.start;
		let length = profile.length;
		let featureZones = this.viewer.scene.featureZones;

		// Compute swath rectangle corners
		let swathCorners = this.getSwathCorners(
			start, dirX, dirY, perpX, perpY, halfSwath, length
		);

		for (let zone of featureZones) {
			let polygon = zone.polygon;
			if (!polygon || polygon.length < 3) { continue; }

			// Test if the swath rectangle intersects the FeatureZone polygon
			if (!this.polygonIntersectsRect(polygon, swathCorners)) { continue; }

			// Compute distance along transect using zone centroid
			let centroid = zone.lidarCentroid || zone.position;
			let cx = centroid.x - start.x;
			let cy = centroid.y - start.y;
			let distanceAlong = cx * dirX + cy * dirY;

			// Clamp to transect range
			distanceAlong = Math.max(0, Math.min(length, distanceAlong));

			// Look up surface elevation at this distance from profile data
			let elevation = this.interpolateElevation(profile.points, distanceAlong);

			// Build datum object if zone has a datumAnchor
			let datum = null;
			if (zone.datumAnchor && !zone.derivedFrom && elevation !== null) {
				let anchor = zone.datumAnchor;
				// offset = known_elevation - raw_pointcloud_elevation
				// shifts all profile elevations into datum-referenced coordinates
				let offset = anchor.datumElevation - elevation;
				datum = {
					offset: offset,
					reference_elevation: anchor.datumElevation,
					reference_label: anchor.label,
				};
			}

			profile.intersectingLandmarks.push({
				landmark_id: zone.name,
				landmark_type: (zone.properties && zone.properties.type) || "unknown",
				label: (zone.properties && (zone.properties.label || zone.properties.id)) || zone.name,
				distance_along: distanceAlong,
				elevation: elevation,
				datum: datum,
			});
		}
	}

	// Detect CurbReveal intersections with the swath rectangle.
	// Computes actual 2D line-segment intersections of curbLine/roadLine
	// with the transect to get precise distance_along values and curb zones.
	detectCurbRevealIntersections(profile, halfSwath, dirX, dirY, perpX, perpY) {
		let start = profile.transectLine.start;
		let length = profile.length;
		let curbReveals = this.viewer.scene.curbReveals;

		let swathCorners = this.getSwathCorners(
			start, dirX, dirY, perpX, perpY, halfSwath, length
		);

		// The transect as a line segment (2D, XY plane)
		let transectStart = { x: start.x, y: start.y };
		let transectEnd = { x: start.x + dirX * length, y: start.y + dirY * length };

		for (let reveal of curbReveals) {
			if (!reveal.initialized) { continue; }

			// Test curb line intersection with swath
			let curbIntersects = this.lineSegmentIntersectsRect(
				reveal.curbLine.start, reveal.curbLine.end, swathCorners
			);

			// Test road line intersection with swath
			let roadIntersects = this.lineSegmentIntersectsRect(
				reveal.roadLine.start, reveal.roadLine.end, swathCorners
			);

			if (!curbIntersects && !roadIntersects) { continue; }

			// Compute actual intersection points with the transect line
			let curbHit = this.lineSegmentIntersection(
				reveal.curbLine.start, reveal.curbLine.end,
				transectStart, transectEnd
			);
			let roadHit = this.lineSegmentIntersection(
				reveal.roadLine.start, reveal.roadLine.end,
				transectStart, transectEnd
			);

			// Project intersection points onto transect for distance_along
			let curbDist = null;
			if (curbHit) {
				curbDist = (curbHit.x - start.x) * dirX + (curbHit.y - start.y) * dirY;
				curbDist = Math.max(0, Math.min(length, curbDist));
			}
			let roadDist = null;
			if (roadHit) {
				roadDist = (roadHit.x - start.x) * dirX + (roadHit.y - start.y) * dirY;
				roadDist = Math.max(0, Math.min(length, roadDist));
			}

			// Get average reveal height in inches
			let avgRevealInches = 0;
			if (reveal.revealData && reveal.revealData.length > 0) {
				let sum = 0;
				for (let d of reveal.revealData) { sum += d.revealInches; }
				avgRevealInches = sum / reveal.revealData.length;
			}

			// If we got both intersections, define a curb zone
			if (curbDist != null && roadDist != null) {
				profile.intersectingCurbReveals.push({
					side: reveal.name,
					distance_along: curbDist,
					road_distance_along: roadDist,
					curb_height: avgRevealInches,
					zone_start: Math.min(curbDist, roadDist),
					zone_end: Math.max(curbDist, roadDist),
				});
			} else {
				// Fallback: only one line intersects, use it as a point marker
				let dist = curbDist != null ? curbDist : roadDist;
				if (dist != null) {
					profile.intersectingCurbReveals.push({
						side: reveal.name,
						distance_along: dist,
						curb_height: avgRevealInches,
					});
				}
			}
		}
	}

	// 2D line-segment intersection returning the intersection point or null.
	// p1-p2 is segment A, p3-p4 is segment B. Each point has {x, y}.
	lineSegmentIntersection(p1, p2, p3, p4) {
		let d1x = p2.x - p1.x;
		let d1y = p2.y - p1.y;
		let d2x = p4.x - p3.x;
		let d2y = p4.y - p3.y;

		let denom = d1x * d2y - d1y * d2x;
		if (Math.abs(denom) < 1e-10) { return null; }

		let t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
		let u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / denom;

		if (t < 0 || t > 1 || u < 0 || u > 1) { return null; }

		return {
			x: p1.x + t * d1x,
			y: p1.y + t * d1y,
		};
	}

	// Compute high/low points and slopes between features
	computeMetrics(profile) {
		let points = profile.points;
		if (points.length === 0) { return; }

		// Find high and low points
		let highPoint = points[0];
		let lowPoint = points[0];

		for (let p of points) {
			if (p.elevation > highPoint.elevation) { highPoint = p; }
			if (p.elevation < lowPoint.elevation) { lowPoint = p; }
		}

		profile.computedMetrics.highPoint = {
			distance_along: highPoint.distanceAlong,
			elevation: highPoint.elevation
		};
		profile.computedMetrics.lowPoint = {
			distance_along: lowPoint.distanceAlong,
			elevation: lowPoint.elevation
		};

		// Compute slopes between adjacent tagged features
		let features = [];

		for (let lm of profile.intersectingLandmarks) {
			features.push({
				label: lm.label,
				distance_along: lm.distance_along,
				elevation: lm.elevation
			});
		}

		for (let cr of profile.intersectingCurbReveals) {
			let elevation = this.interpolateElevation(points, cr.distance_along);
			features.push({
				label: cr.side,
				distance_along: cr.distance_along,
				elevation: elevation
			});
		}

		// Sort by distance along transect
		features.sort((a, b) => a.distance_along - b.distance_along);

		let slopes = [];
		for (let i = 0; i < features.length - 1; i++) {
			let from = features[i];
			let to = features[i + 1];
			let dist = to.distance_along - from.distance_along;

			if (dist > 0.001) {
				let slopePercent = ((to.elevation - from.elevation) / dist) * 100;
				slopes.push({
					from_label: from.label,
					to_label: to.label,
					slope_percent: slopePercent,
					distance: dist
				});
			}
		}

		profile.computedMetrics.slopes = slopes;
	}

	// Apply datum offset if an intersecting landmark has datum data
	applyDatumOffset(profile) {
		// Find first landmark with a datum anchor (Feature 2)
		let datumLandmark = profile.intersectingLandmarks.find(lm => lm.datum !== null);
		if (!datumLandmark) { return; }

		let offset = datumLandmark.datum.offset;
		profile.datumOffset = {
			applied: true,
			referenceLandmarkId: datumLandmark.landmark_id,
			offsetValue: offset
		};

		// Shift all elevations
		for (let p of profile.points) { p.elevation += offset; }
		for (let p of profile.pointsRaw) { p.elevation += offset; }

		if (profile.computedMetrics.highPoint) {
			profile.computedMetrics.highPoint.elevation += offset;
		}
		if (profile.computedMetrics.lowPoint) {
			profile.computedMetrics.lowPoint.elevation += offset;
		}

		for (let lm of profile.intersectingLandmarks) {
			if (lm.elevation !== null) { lm.elevation += offset; }
		}
	}

	// Add 3D transect line and swath rectangle visualization
	addTransectVisualization(profile) {
		let start = profile.transectLine.start;
		let end = profile.transectLine.end;
		profile._lineObjects = [];

		// Transect line (green) using centroid-offset pattern
		let midpoint = new THREE.Vector3(
			(start.x + end.x) / 2,
			(start.y + end.y) / 2,
			(start.z + end.z) / 2
		);

		let startLocal = start.clone().sub(midpoint);
		let endLocal = end.clone().sub(midpoint);

		let lineGeom = new THREE.BufferGeometry().setFromPoints([startLocal, endLocal]);
		let lineMat = new THREE.LineBasicMaterial({
			color: 0x00cc66, depthTest: false, depthWrite: false
		});
		let lineObj = new THREE.Line(lineGeom, lineMat);
		lineObj.position.copy(midpoint);
		lineObj.renderOrder = 1;
		this.toolScene.add(lineObj);
		profile._lineObjects.push(lineObj);

		// Swath rectangle (semi-transparent green outline)
		let halfSwath = profile.swathWidth / 2;
		let dx = end.x - start.x;
		let dy = end.y - start.y;
		let len = Math.sqrt(dx * dx + dy * dy);
		if (len < 0.001) { return; }

		let dirX = dx / len;
		let dirY = dy / len;
		let perpX = -dirY;
		let perpY = dirX;

		// Average Z for rectangle display
		let avgZ = (start.z + end.z) / 2;

		let corners = [
			new THREE.Vector3(
				start.x + perpX * halfSwath - midpoint.x,
				start.y + perpY * halfSwath - midpoint.y,
				avgZ - midpoint.z
			),
			new THREE.Vector3(
				end.x + perpX * halfSwath - midpoint.x,
				end.y + perpY * halfSwath - midpoint.y,
				avgZ - midpoint.z
			),
			new THREE.Vector3(
				end.x - perpX * halfSwath - midpoint.x,
				end.y - perpY * halfSwath - midpoint.y,
				avgZ - midpoint.z
			),
			new THREE.Vector3(
				start.x - perpX * halfSwath - midpoint.x,
				start.y - perpY * halfSwath - midpoint.y,
				avgZ - midpoint.z
			),
		];

		let rectGeom = new THREE.BufferGeometry().setFromPoints([
			corners[0], corners[1], corners[2], corners[3], corners[0]
		]);
		let rectMat = new THREE.LineBasicMaterial({
			color: 0x00cc66, depthTest: false, depthWrite: false, transparent: true, opacity: 0.5
		});
		let rectObj = new THREE.Line(rectGeom, rectMat);
		rectObj.position.copy(midpoint);
		rectObj.renderOrder = 1;
		this.toolScene.add(rectObj);
		profile._lineObjects.push(rectObj);
	}

	// --- Geometry helpers ---

	getSwathCorners(start, dirX, dirY, perpX, perpY, halfSwath, length) {
		// Returns 4 corners of the swath rectangle in world XY coords
		return [
			[start.x + perpX * halfSwath, start.y + perpY * halfSwath],
			[start.x + dirX * length + perpX * halfSwath, start.y + dirY * length + perpY * halfSwath],
			[start.x + dirX * length - perpX * halfSwath, start.y + dirY * length - perpY * halfSwath],
			[start.x - perpX * halfSwath, start.y - perpY * halfSwath]
		];
	}

	// Test if a 2D polygon intersects a 2D rectangle (both as arrays of [x,y])
	polygonIntersectsRect(polygon, rect) {
		// Test 1: any polygon vertex inside rect?
		for (let v of polygon) {
			if (this.pointInQuad(v[0], v[1], rect)) { return true; }
		}

		// Test 2: any rect vertex inside polygon?
		for (let v of rect) {
			if (this.pointInPolygon(v[0], v[1], polygon)) { return true; }
		}

		// Test 3: any edge-edge intersection?
		let polyLen = polygon.length;
		let rectLen = rect.length;

		for (let i = 0; i < polyLen; i++) {
			let pA = polygon[i];
			let pB = polygon[(i + 1) % polyLen];

			for (let j = 0; j < rectLen; j++) {
				let rA = rect[j];
				let rB = rect[(j + 1) % rectLen];

				if (this.lineSegmentsIntersect2D(
					pA[0], pA[1], pB[0], pB[1],
					rA[0], rA[1], rB[0], rB[1]
				)) {
					return true;
				}
			}
		}

		return false;
	}

	// Test if a line segment intersects a rectangle (4-corner quad)
	lineSegmentIntersectsRect(lineStart, lineEnd, rect) {
		let lsx = lineStart.x, lsy = lineStart.y;
		let lex = lineEnd.x, ley = lineEnd.y;

		// Check if either endpoint is inside
		if (this.pointInQuad(lsx, lsy, rect)) { return true; }
		if (this.pointInQuad(lex, ley, rect)) { return true; }

		// Check edge intersections
		let rectLen = rect.length;
		for (let j = 0; j < rectLen; j++) {
			let rA = rect[j];
			let rB = rect[(j + 1) % rectLen];

			if (this.lineSegmentsIntersect2D(
				lsx, lsy, lex, ley,
				rA[0], rA[1], rB[0], rB[1]
			)) {
				return true;
			}
		}

		return false;
	}

	// 2D line segment intersection test
	lineSegmentsIntersect2D(ax1, ay1, ax2, ay2, bx1, by1, bx2, by2) {
		let dax = ax2 - ax1;
		let day = ay2 - ay1;
		let dbx = bx2 - bx1;
		let dby = by2 - by1;

		let denom = dax * dby - day * dbx;
		if (Math.abs(denom) < 1e-10) { return false; }

		let t = ((bx1 - ax1) * dby - (by1 - ay1) * dbx) / denom;
		let u = ((bx1 - ax1) * day - (by1 - ay1) * dax) / denom;

		return t >= 0 && t <= 1 && u >= 0 && u <= 1;
	}

	// Point-in-quad test (4-corner polygon)
	pointInQuad(px, py, quad) {
		return this.pointInPolygon(px, py, quad);
	}

	// Ray-casting point-in-polygon test
	pointInPolygon(px, py, polygon) {
		let inside = false;
		let n = polygon.length;

		for (let i = 0, j = n - 1; i < n; j = i++) {
			let xi = polygon[i][0];
			let yi = polygon[i][1];
			let xj = polygon[j][0];
			let yj = polygon[j][1];

			if (((yi > py) !== (yj > py)) &&
				(px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
				inside = !inside;
			}
		}

		return inside;
	}

	// Interpolate elevation from profile points at a given distance
	interpolateElevation(points, distance) {
		if (points.length === 0) { return 0; }
		if (points.length === 1) { return points[0].elevation; }

		// Find bracketing points
		for (let i = 0; i < points.length - 1; i++) {
			let p0 = points[i];
			let p1 = points[i + 1];

			if (distance >= p0.distanceAlong && distance <= p1.distanceAlong) {
				let t = (distance - p0.distanceAlong) / (p1.distanceAlong - p0.distanceAlong);
				return p0.elevation + t * (p1.elevation - p0.elevation);
			}
		}

		// Extrapolate from nearest end
		if (distance <= points[0].distanceAlong) {
			return points[0].elevation;
		}
		return points[points.length - 1].elevation;
	}

	// R-7 linear interpolation percentile (same as ElevationZoneTool)
	percentile(sortedArray, p) {
		let n = sortedArray.length;
		if (n === 0) { return 0; }
		if (n === 1) { return sortedArray[0]; }

		let index = p * (n - 1);
		let lower = Math.floor(index);
		let upper = Math.ceil(index);
		let weight = index - lower;

		if (upper >= n) { return sortedArray[n - 1]; }
		return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
	}

	update(e) {
		let dominated = this.viewer.inputHandler.scene === this.toolScene;
		let camera = this.viewer.scene.getActiveCamera();
		this.light.position.copy(camera.position);
	}

	render(e) {
		let viewer = e.viewer;
		let renderer = viewer.renderer;
		let camera = viewer.scene.getActiveCamera();

		viewer.renderer.render(this.toolScene, camera);
	}
}
